"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  BookOpen,
  ChevronRight,
  FileText,
  Layers3,
  Loader2,
  Search,
  Settings2,
} from "lucide-react";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ContentTypeIcon,
  getContentMeta,
} from "@/components/learning-paths/contentMeta";
import { NAV_SEARCH_OPEN_EVENT } from "@/lib/navigation/nav-search-open";
import { isHrefVisible } from "@/lib/plugins";
import { useSession } from "next-auth/react";
import { cn } from "@/lib/utils";

const QUICK_LINKS = [
  {
    id: "quick-home",
    group: "Quick links",
    kind: "page",
    badge: "Page",
    title: "Home",
    subtitle: "Today, priorities, and coaching",
    href: "/",
  },
  {
    id: "quick-learning-paths",
    group: "Quick links",
    kind: "learning_path",
    badge: "Page",
    title: "Learning paths",
    subtitle: "Assigned paths and progress",
    href: "/dashboard/learning-paths",
  },
  {
    id: "quick-tests",
    group: "Quick links",
    kind: "test",
    badge: "Page",
    title: "Tests",
    subtitle: "Knowledge checks and results",
    href: "/dashboard/tests",
  },
];

const KIND_ICON = {
  assignment: Layers3,
  content: Layers3,
  learning_path: BookOpen,
  resource: BookOpen,
  test: FileText,
  admin: Settings2,
  page: Search,
};

/**
 * Bucket results by group while keeping the server's relevance order: a group
 * appears as high as its best result does. A fixed group order would bury the
 * closest match — typing a page's name would still list assignments first.
 */
function groupResults(results) {
  const grouped = new Map();

  results.forEach((result, index) => {
    const group = result.group || "Results";
    if (!grouped.has(group)) grouped.set(group, { bestIndex: index, items: [] });
    grouped.get(group).items.push(result);
  });

  return Array.from(grouped.entries())
    .sort(([, a], [, b]) => a.bestIndex - b.bestIndex)
    .map(([group, { items }]) => [group, items]);
}

function ResultIcon({ result }) {
  if (result.contentType) {
    const meta = getContentMeta(result.contentType);
    return (
      <span
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
          meta.tile,
        )}
      >
        <ContentTypeIcon
          type={result.contentType}
          iconSize="h-4 w-4"
          title={result.badge}
        />
      </span>
    );
  }

  const Icon = KIND_ICON[result.kind] || Search;
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-600">
      <Icon className="h-4 w-4" />
    </span>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-2 px-3 py-3">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="flex items-center gap-3 rounded-lg border border-transparent px-2 py-2"
        >
          <div className="h-9 w-9 rounded-lg bg-gray-100" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded bg-gray-100" />
            <div className="h-2.5 w-2/3 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NavSearchDialog({ className }) {
  const router = useRouter();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Set when the search was opened from a nav dropdown: the user was reading a
  // list of destinations, so destinations lead the results. Cleared on close.
  const [pagesFirst, setPagesFirst] = useState(false);
  // Which menu that was. Sent to the server, where it breaks ties between pages
  // that share a name across sections — nothing more.
  const [preferredSection, setPreferredSection] = useState("");
  // Guards one-event-per-session logging (see the commit effect below).
  const committedRef = useRef(false);
  const inputRef = useRef(null);
  // True when this open was seeded with a keystroke from a nav menu. That
  // character is already in the input, so the caret belongs after it rather
  // than over it (see handleOpenAutoFocus).
  const seededRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Handoff from the header's nav dropdowns: typing into an open menu closes it
  // and lands here instead, carrying the keystroke that started it.
  useEffect(() => {
    const onOpenRequest = (event) => {
      const detail = event.detail || {};
      setPagesFirst(Boolean(detail.pagesFirst));
      setPreferredSection(detail.section || "");
      setQuery(detail.query || "");
      seededRef.current = Boolean(detail.query);
      setOpen(true);
    };

    window.addEventListener(NAV_SEARCH_OPEN_EVENT, onOpenRequest);
    return () =>
      window.removeEventListener(NAV_SEARCH_OPEN_EVENT, onOpenRequest);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();
    const trimmedQuery = query.trim();
    const searchParams = new URLSearchParams({
      // Send what was debounced on, so a stray leading space can't produce a
      // different request than the one the debounce decided about.
      q: trimmedQuery,
      limit: "5",
      ...(preferredSection ? { section: preferredSection } : {}),
    });

    setIsLoading(true);
    setError("");
    setResults([]);

    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/nav-search?${searchParams}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Search failed");
        }

        const payload = await response.json();
        setResults(Array.isArray(payload.results) ? payload.results : []);
      } catch (fetchError) {
        if (fetchError.name !== "AbortError") {
          setError("Search is unavailable right now.");
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, trimmedQuery ? 180 : 0);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [open, preferredSection, query]);

  const displayResults = useMemo(() => {
    // Search results are already plugin-filtered server-side; QUICK_LINKS is a
    // hardcoded list here, so it gets the same check rather than becoming the
    // one place a disabled plugin can still be reached (SAL-2912).
    const quickLinks = QUICK_LINKS.filter((link) =>
      isHrefVisible(session?.workspace, link.href),
    );
    const base = query.trim() ? results : [...results, ...quickLinks];
    if (!pagesFirst) return base;

    // Stable partition rather than a re-sort: page links move ahead as a block
    // and everything keeps the relevance order the server decided on. Because
    // groupResults ranks a group by its best result, this alone puts the
    // "Pages" heading at the top of the list.
    return [
      ...base.filter((result) => result.kind === "page"),
      ...base.filter((result) => result.kind !== "page"),
    ];
  }, [pagesFirst, query, results, session?.workspace]);

  const groupedResults = useMemo(
    () => groupResults(displayResults),
    [displayResults],
  );

  // Logs the final search of a session — the page the user was on, the result
  // count, and the opened result (if any). Only the settled query is saved, not
  // each keystroke. Guarded so close + select don't double-log.
  const commit = (selectedResult) => {
    if (committedRef.current) return;
    const trimmed = query.trim();
    if (!trimmed) return;
    committedRef.current = true;

    const payload = {
      query: trimmed,
      resultCount: results.length,
      pagePath:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}`
          : undefined,
      selectedResult: selectedResult?.id
        ? {
            id: selectedResult.id,
            title: selectedResult.title,
            href: selectedResult.href,
            kind: selectedResult.kind,
            group: selectedResult.group,
            badge: selectedResult.badge,
          }
        : null,
    };

    void fetch("/api/nav-search-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* non-blocking analytics */
    });
  };

  // One event per search session: opening arms the guard; any close path
  // (Esc, outside-click, ⌘K) records the final typed query. Selecting a result
  // commits early in handleSelect, so this close commit is then a no-op.
  useEffect(() => {
    if (open) {
      committedRef.current = false;
    } else {
      commit(null);
      // The next open is a plain ⌘K until something asks for pages first again.
      setPagesFirst(false);
      setPreferredSection("");
      seededRef.current = false;
    }
    // commit intentionally reads the latest query/results via closure on the
    // render where `open` flips to false.
  }, [open]);

  /**
   * Radix's focus scope auto-focuses the first input and *selects* its
   * contents. For a plain ⌘K open that is what you want — the stale query from
   * last time is replaced as soon as you type. But when a nav menu handed off
   * the keystroke that opened this dialog, that character is the query so far,
   * and selecting it means the next key typed overwrites it. Focus the input
   * ourselves so a seeded open puts the caret after the seed instead.
   */
  const handleOpenAutoFocus = (event) => {
    const input = inputRef.current;
    if (!input) return;

    event.preventDefault();
    input.focus({ preventScroll: true });

    if (seededRef.current) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    } else {
      input.select();
    }
  };

  const handleSelect = (result) => {
    const href = result?.href;
    if (!href) return;
    commit(result);
    setOpen(false);
    setQuery("");
    router.push(href);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 transition-colors hover:bg-brand-100 hover:text-brand-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-800 focus-visible:ring-offset-2",
          className,
        )}
        aria-label="Search"
        title="Search (⌘K)"
      >
        <Search className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          hideCloseButton
          onOpenAutoFocus={handleOpenAutoFocus}
          className="top-[12vh] max-h-[78vh] max-w-2xl translate-y-0 overflow-hidden rounded-2xl border-gray-200 bg-white p-0 !text-gray-900 shadow-[0_24px_70px_-20px_rgba(17,24,39,0.28)] sm:rounded-2xl"
        >
          <DialogTitle className="sr-only">Search</DialogTitle>
          <Command shouldFilter={false} className="rounded-2xl bg-white !text-gray-900">
            <CommandInput
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder={
                pagesFirst
                  ? "Search pages, assignments, resources..."
                  : "Search assignments, learning paths, resources..."
              }
              className="h-12 !text-gray-900 placeholder:!text-gray-400"
            />
            <CommandList className="max-h-[58vh] min-h-[220px] overflow-y-auto">
              {isLoading && results.length === 0 && query.trim() ? (
                <LoadingRows />
              ) : null}

              {!isLoading && error ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  {error}
                </div>
              ) : null}

              {!isLoading && !error && groupedResults.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  No matches found.
                </div>
              ) : null}

              {groupedResults.map(([group, items]) => (
                <CommandGroup
                  key={group}
                  heading={group}
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.18em] [&_[cmdk-group-heading]]:!text-gray-400"
                >
                  {items.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={result.id}
                      onSelect={() => handleSelect(result)}
                      className="group cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 !text-gray-900 !opacity-100 transition-colors hover:bg-gray-50 aria-selected:bg-blue-50/80 aria-selected:!text-gray-900 data-disabled:pointer-events-auto data-disabled:!opacity-100"
                    >
                      <ResultIcon result={result} />
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-sm font-semibold !text-gray-900">
                            {result.title}
                          </span>
                          {result.badge ? (
                            <span className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider !text-gray-500">
                              {result.badge}
                            </span>
                          ) : null}
                        </div>
                        {result.subtitle ? (
                          <p className="mt-0.5 truncate text-xs !text-gray-500">
                            {result.subtitle}
                          </p>
                        ) : null}
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-all duration-200 group-aria-selected:translate-x-0.5 group-aria-selected:text-gray-400" />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}

              {isLoading && results.length > 0 ? (
                <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Updating results
                </div>
              ) : null}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
